import { axiosPatch } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import type { FormControlProp } from '@/components';
import { useInvalidateQuery } from '@/hooks';
import Big from 'big.js';
import { useWatch } from 'react-hook-form';
import type { z } from 'zod';
import {
  FundAdditionalIncomeAndExpenditurePriorMutationSchema,
  FundAdditionalIncomeAndExpenditureSchema,
  type PeriodScopeType,
} from '../projects/_models/fund-additiona-income-and-expenditure';
import { queryKeyMap } from '../review-sheet/utils/query-keys';

type WatchProps = {
  control: FormControlProp<
    z.infer<typeof FundAdditionalIncomeAndExpenditureSchema>
  >;
  type: PeriodScopeType;
};

export const useIncomeSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: [
      'grantedAmountByTheCountry',
      'investmentIncome',
      'businessIncome',
      'otherIncomeAmount',
    ],
  });
  const estimatedThisYearValues = useWatch({
    control,
    name: [
      'estimatedGrantedAmountByTheCountry',
      'estimatedInvestmentIncome',
      'estimatedBusinessIncome',
      'estimatedOtherIncomeAmount',
    ],
  });
  const estimatedNextYearValues = useWatch({
    control,
    name: [
      'estimatedGrantedAmountByTheCountryNextYear',
      'estimatedInvestmentIncomeNextYear',
      'estimatedBusinessIncomeNextYear',
      'estimatedOtherIncomeAmountNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : estimatedNextYearValues;

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useExpenditureSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: ['businessExpense', 'administrativeExpense'],
  });

  const estimatedThisYearValues = useWatch({
    control,
    name: ['estimatedBusinessExpense', 'estimatedAdministrativeExpense'],
  });

  const estimatedNextYearValues = useWatch({
    control,
    name: [
      'estimatedBusinessExpenseNextYear',
      'estimatedAdministrativeExpenseNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : estimatedNextYearValues;

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useIncomeAndExpenditureSummaryByTypeWatch = ({
  type,
  control,
}: WatchProps): Big => {
  const incomeSummaryByType = useIncomeSummaryByTypeWatch({ type, control });
  const expenditureSummaryByType = useExpenditureSummaryByTypeWatch({
    type,
    control,
  });
  const amountReturnedToTheOrganization = useWatch({
    control,
    name: 'amountReturnedToTheOrganization',
  });
  const estimatedAmountReturnedToTheOrganization = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheOrganization',
  });
  const estimatedAmountReturnedToTheOrganizationNextYear = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheOrganizationNextYear',
  });
  const beginningOfTheFundBalance = useWatch({
    control,
    name: 'beginningOfTheFundBalance',
  });
  const amountReturnedToTheNationalTreasury = useWatch({
    control,
    name: 'amountReturnedToTheNationalTreasury',
  });
  const estimatedAmountReturnedToTheNationalTreasury = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheNationalTreasury',
  });
  const beginningOfTheEstimatedFundBalanceNextYear = useWatch({
    control,
    name: 'beginningOfTheEstimatedFundBalanceNextYear',
  });
  const estimatedAmountReturnedToTheNationalTreasuryNextYear = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheNationalTreasuryNextYear',
  });

  switch (type) {
    case 'previous-year-performance':
      return new Big(0)
        .plus(beginningOfTheFundBalance ?? 0)
        .plus(incomeSummaryByType)
        .minus(expenditureSummaryByType)
        .minus(amountReturnedToTheNationalTreasury ?? 0)
        .minus(amountReturnedToTheOrganization ?? 0);
    case 'estimated-this-year':
      return new Big(0)
        .plus(beginningOfTheFundBalance ?? 0)
        .plus(incomeSummaryByType)
        .minus(expenditureSummaryByType)
        .minus(estimatedAmountReturnedToTheNationalTreasury ?? 0)
        .minus(estimatedAmountReturnedToTheOrganization ?? 0);
    default:
      return new Big(0)
        .plus(beginningOfTheEstimatedFundBalanceNextYear ?? 0)
        .plus(incomeSummaryByType)
        .minus(expenditureSummaryByType)
        .minus(estimatedAmountReturnedToTheNationalTreasuryNextYear ?? 0)
        .minus(estimatedAmountReturnedToTheOrganizationNextYear ?? 0);
  }
};

export const useClericalExpenditureSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: ['choreExpensesNotFromThisFund', 'personnelExpensesNotFromThisFund'],
  });
  const estimatedThisYearValues = useWatch({
    control,
    name: [
      'estimatedChoreExpensesNotFromThisFund',
      'estimatedPersonnelExpensesNotFromThisFund',
    ],
  });
  const expensesNotFromThisFundNextYearValues = useWatch({
    control,
    name: [
      'estimatedChoreExpensesNotFromThisFundNextYear',
      'estimatedPersonnelExpensesNotFromThisFundNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : expensesNotFromThisFundNextYearValues;

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useUpdateFundAdditionalIncomeAndExpenditure = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      projectId,
      fundAdditionalIncomeAndExpenditure,
    }: {
      projectId: string;
      fundAdditionalIncomeAndExpenditure: z.infer<
        typeof FundAdditionalIncomeAndExpenditureSchema
      >;
    }) => {
      const parsed = FundAdditionalIncomeAndExpenditureSchema.parse(
        fundAdditionalIncomeAndExpenditure,
      );
      const payload = {
        fundAdditionalIncomeAndExpenditure: {
          ...parsed,
        },
      };

      const res = await axiosPatch(`/api/projects/${projectId}/`, payload);
      await Promise.all([
        invalidate(queryKeyMap.project()),
        invalidate(queryKeyMap.projects()),
      ]);
      return res;
    },
  });
};

export const useUpdateFundAdditionalIncomeAndExpenditurePrior = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      fundAdditionalIncomeAndExpenditure,
    }: {
      fundAdditionalIncomeAndExpenditure: z.infer<
        typeof FundAdditionalIncomeAndExpenditurePriorMutationSchema
      >;
    }) => {
      const { id, ...payload } =
        FundAdditionalIncomeAndExpenditurePriorMutationSchema.parse(
          fundAdditionalIncomeAndExpenditure,
        );

      const res = await axiosPatch(
        `/api/projects/fund-additional-income-and-expenditure/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.project()),
        invalidate(queryKeyMap.projects()),
      ]);
      return res;
    },
  });
};
